import React, { memo, useEffect } from "react";
import { compose } from "redux";
import { connect, useSelector } from "react-redux";
import { createStructuredSelector } from "reselect";
import { IntlProvider } from "react-intl";
import get from "lodash/get";
import {
  fetchReferralCode,
  fetchAppSettings,
  fetchGlobalSettings,
  fetchDefaultAnalytics,
  fetchCustomerInfo,
  fetchPhones,
  changeFormField,
  selectPlan,
  fetchMyInfoDetails
} from "ducks/actions";
import {
  makeSelectMessages,
  makeSelectIsFetchingRequired,
  makeSelectRequiresMaintenance,
  makeSelectReferralCode,
  makeSelectCustomerInfo,
  makeSelectPlanName,
  makeSelectPhones,
  makeSelectPhonesForm,
  makeSelectMyInfoCode,
  makeSelectChannelPaymentDisable,
  makeSelectGlobalFormatSelector
} from "ducks/selectors";
import {
  COMBO_CHANNEL,
  DEFAULT_KEYS_TO_FETCH,
  forms,
  paths
} from "utils/constants";
import {
  urlParam,
  upper,
  gotoUrl,
  getChannelFromUrl,
  getPaymentOption
} from "utils/helpers";
import { appGlobalSettingsSelector } from "ducks/selectors/appGlobalSettings";
import isEmpty from "lodash/isEmpty";
import find from "lodash/find";
import round from "lodash/round";
import { useI18nFormatterListener } from "hooks/useI18nFormatterListener";
import { invokeLocale } from "utils/localeInvoker";
import { useFlags } from "launchdarkly-react-client-sdk";
import { setItem } from "utils/localStorage";

export const DataLoader = props => {
  const {
    requiresFetching,
    render,
    messages,
    fetchData,
    fetchGlobalSettings,
    referralCode,
    validateReferralCode,
    customerInfo,
    fetchCustomerInfo,
    fetchPhones,
    planName,
    phones = [],
    selectPlan,
    setSelectedPhone,
    eligiblePlans,
    phonesForm = {},
    requiresMaintenance,
    paymentDisable
  } = props;
  const pvid = urlParam("pvid");
  const paymentTerm = urlParam("payment_term");
  const paymentOption = urlParam("payment_type") || urlParam("ppo");
  const paymentType = getPaymentOption(paymentOption, paymentDisable);
  const selectedPlanName = urlParam("plan_selection") || urlParam("plan_name");
  const channel = getChannelFromUrl();
  const hasPhones = phones?.length;
  const selectedPaymentTerm = phonesForm?.paymentTerm;
  const selectedPhoneVariant = phonesForm?.phoneVariant?.id;
  const globalSettings = useSelector(appGlobalSettingsSelector);
  const { effEvamEvents = false } = useFlags();
  useI18nFormatterListener();

  // const fetchLocalization = () => {
  //   if (!requiresFetching) {
  //     dispatch(fetchLocale(maintenanceOnError));
  //   }
  // };

  useEffect(
    () => {
      if (requiresFetching || urlParam("relaunch")) {
        fetchGlobalSettings();
      }
      setItem("effEvamEvents", effEvamEvents);
      // window.addEventListener("load", fetchLocalization);
      // return () => {
      //   window.removeEventListener("load", fetchLocalization);
      // };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  useEffect(
    () => {
      if (globalSettings) {
        if (requiresFetching || urlParam("relaunch")) {
          fetchData();
        }
        const account_number = upper(
          urlParam("account_no") || urlParam("accountNo")
        );
        const ott = urlParam("ott");
        if (
          (account_number &&
            account_number !== get(customerInfo, "account_number")) ||
          (ott && isEmpty(customerInfo))
        ) {
          fetchCustomerInfo(account_number, ott);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [globalSettings]
  );

  useEffect(() => {
    if (channel === COMBO_CHANNEL && !requiresFetching) {
      fetchPhones();
    }
  }, [requiresFetching, channel, fetchPhones]);

  // validateReferralCode on change of planName
  useEffect(
    () => {
      // the authentication url check is to avoid considering code url params in social media redirection
      const isAuthenticationRedirection = window.location.pathname.includes(
        "authentication"
      );
      const referralCodeParam = !isAuthenticationRedirection
        ? upper(urlParam("code") || urlParam("referral"))
        : "";
      const appliedReferralCode = get(referralCode, "value");
      // give priority to appliedReferralCode
      const code = appliedReferralCode || referralCodeParam;

      const myInfoCondition = !isEmpty(code) && !isEmpty(urlParam("state")); //myinfo redirects with ?code=aaa&state=bbb . We treat the presence of these 2 params tgt as myInfo condition.

      if (
        code &&
        code !== appliedReferralCode &&
        planName &&
        !myInfoCondition
      ) {
        validateReferralCode(code, planName, urlParam("pvid"));
        // Remove promocode query param, once it is applied.
        if (referralCodeParam) {
          gotoUrl(window.location.pathname, true, {
            code: null,
            referral: null,
            reset: null
          });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [planName]
  );

  useEffect(() => {
    if (channel === COMBO_CHANNEL && !isEmpty(phones)) {
      if (selectedPlanName !== planName) {
        const selectedPlan = find(eligiblePlans, { name: selectedPlanName });
        selectedPlan && selectPlan(selectedPlan);
      }
      if (pvid) {
        let allPhoneVariants = [];
        phones.forEach(phone => {
          if (
            !phone.outOfStock &&
            !phone.comingSoon &&
            !isEmpty(phone.allPhoneVariants)
          ) {
            allPhoneVariants = [...allPhoneVariants, ...phone.allPhoneVariants];
          }
        });
        const getPhoneVariant = find(allPhoneVariants, { id: pvid });
        if (getPhoneVariant) {
          setSelectedPhone("phoneVariant", getPhoneVariant);
          setSelectedPhone("phoneColorCode", getPhoneVariant.colorCode);
          setSelectedPhone("phoneColorName", getPhoneVariant.colorName);
          setSelectedPhone("phoneCapacity", getPhoneVariant.capacity);
          setSelectedPhone("phonePaymentOption", paymentType);

          if (paymentType === "installment") {
            const {
              minInstallmentMonth,
              maxInstallmentMonth,
              installmentStep
            } = getPhoneVariant;
            if (paymentTerm) {
              const paymentMonth = parseInt(paymentTerm);
              const value =
                paymentMonth >= maxInstallmentMonth
                  ? maxInstallmentMonth
                  : paymentMonth <= minInstallmentMonth
                  ? minInstallmentMonth
                  : paymentMonth / installmentStep === 0
                  ? paymentMonth
                  : round(paymentMonth / installmentStep) * installmentStep;
              setSelectedPhone("paymentTerm", value);
            } else {
              setSelectedPhone("paymentTerm", maxInstallmentMonth);
            }
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasPhones,
    pvid,
    selectedPlanName,
    eligiblePlans,
    paymentType,
    selectedPaymentTerm,
    selectedPhoneVariant,
    planName
  ]);

  return (
    <IntlProvider locale="en" messages={messages}>
      {render({ loading: requiresFetching, requiresMaintenance })}
    </IntlProvider>
  );
};

const mapStateToProps = createStructuredSelector({
  messages: makeSelectMessages(),
  requiresFetching: makeSelectIsFetchingRequired(DEFAULT_KEYS_TO_FETCH),
  requiresMaintenance: makeSelectRequiresMaintenance(DEFAULT_KEYS_TO_FETCH),
  referralCode: makeSelectReferralCode(),
  customerInfo: makeSelectCustomerInfo(),
  planName: makeSelectPlanName(),
  phones: makeSelectPhones(),
  phonesForm: makeSelectPhonesForm(),
  myInfoCode: makeSelectMyInfoCode(),
  paymentDisable: makeSelectChannelPaymentDisable(),
  globalFormat: makeSelectGlobalFormatSelector()
});

const maintenanceOnError = (result, error, status, _, key, requestURL) => {
  if (result?.maintenanceMode || !isEmpty(error)) {
    if (window.newrelic) {
      window.newrelic.addPageAction("maintenance", {
        apiFailed: key,
        apiUrl: requestURL,
        errorCode: status
      });
    }
    setTimeout(() => {
      gotoUrl(result?.maintenancePage || paths.MAINTENANCE);
    });
  }
};

export function mapDispatchToProps(dispatch) {
  return {
    fetchGlobalSettings: () => {
      dispatch(fetchGlobalSettings(maintenanceOnError));
      invokeLocale(dispatch, maintenanceOnError);
      dispatch(fetchDefaultAnalytics(maintenanceOnError));
    },
    fetchData: () => {
      dispatch(fetchAppSettings(maintenanceOnError));
    },
    fetchPhones: () => {
      dispatch(fetchPhones());
    },
    fetchCustomerInfo: (account_number, ott) =>
      dispatch(fetchCustomerInfo({ account_number, ott })),
    validateReferralCode: (referral_code, plan_name, device_sku = null) => {
      const data = {
        referral_code,
        plan_name,
        ...(device_sku && { device_sku: true })
      };
      dispatch(fetchReferralCode(data));
    },
    setSelectedPhone: (field, value) => {
      dispatch(
        changeFormField({
          form: forms.PHONES_FORM,
          field: field,
          value: value
        })
      );
    },
    selectPlan: plan => dispatch(selectPlan(plan)),
    fetchMyInfoDetails: (params, callback) => {
      dispatch(fetchMyInfoDetails(params, callback));
    }
  };
}

DataLoader.defaultProps = {
  match: {},
  requiresFetching: false,
  messages: [],
  render: () => {}
};

const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default compose(withConnect, memo)(DataLoader);
